import { Link, Navigate, useParams, useSearchParams } from "react-router";

import { RegistryFailure, RegistryLoading } from "@/components/RegistryLookupState";
import { RegistryRecordCard } from "@/components/RegistryRecordCard";
import { Card, PageHeader } from "@/components/ui";
import { NETWORK_NAME } from "@/lib/config";
import { usePageMeta } from "@/lib/page-meta";
import { resolveRegistryId } from "@/lib/registry/ids";
import { useRegistryLookup } from "@/lib/registry/use-lookup";

import { NotFoundPage } from "./NotFoundPage";

const CONTEXT = "Utility";

export function UtilityPage() {
  const { registryId: raw = "" } = useParams();
  const [searchParams] = useSearchParams();

  usePageMeta({
    title: `Utility ${decodeURIComponent(raw)}`,
    description: "Module, vault, versi, dan status sebuah utility ASETQU.",
  });

  const resolved = resolveRegistryId(raw);

  /*
    An explicit `?version=` is passed straight through to the server, which
    defaults to the newest registered version when it is absent — a bare link
    should show what is current, not the original generation.

    Anything that is not a positive integer is dropped rather than forwarded:
    the server would answer 400, and a malformed query parameter in a shared
    link should not turn the page into an error.
  */
  const requested = Number(searchParams.get("version"));
  const version =
    Number.isInteger(requested) && requested >= 1 ? requested : undefined;

  const lookup = useRegistryLookup(resolved ?? undefined, version);

  if (!resolved) return <NotFoundPage />;
  if (lookup.status === "loading") return <RegistryLoading context={CONTEXT} />;

  if (!lookup.lookup.ok) {
    if (lookup.lookup.failure.kind === "unconfigured") {
      return <Navigate to={`/verify/${raw}`} replace />;
    }
    return <RegistryFailure context={CONTEXT} failure={lookup.lookup.failure} />;
  }

  const record = lookup.lookup.record;
  if (record.registryType === "ExternalVendor") {
    return <Navigate to={`/vendor/${raw}`} replace />;
  }

  const versions = record.versions;

  return (
    <div className="page-shell">
      <PageHeader
        context={CONTEXT}
        title={record.name ?? "Utility"}
        description={`Module dan vault aktif menurut UtilityRegistry di ${NETWORK_NAME}.`}
      />

      <RegistryRecordCard record={record} />

      {versions.length > 1 && (
        <Card title="Riwayat versi">
          <p className="mb-3 text-sm text-ink-muted">
            Setiap versi adalah deployment terpisah. Versi <strong>Deprecated</strong> tidak
            menerima transaksi baru, tetapi tetap wajib dapat menyelesaikan refund dan settlement
            yang sudah berjalan — jangan menganggapnya mati.
          </p>
          <ul className="flex flex-wrap gap-2">
            {versions.map((candidate) => (
              <li key={candidate}>
                <Link
                  className={`inline-flex rounded-lg px-3 py-1 text-sm ring-1 ring-inset ${
                    candidate === record.version
                      ? "text-brand ring-brand"
                      : "text-ink-muted ring-line hover:text-brand hover:ring-brand"
                  }`}
                  to={`/utility/${raw}?version=${candidate}`}
                >
                  v{candidate}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {record.permanentVault && (
        <Card title="Tentang angka terkunci">
          <p className="text-sm leading-relaxed text-ink-muted">
            Vault permanen tidak punya jalur keluar sama sekali — tidak ada withdraw, sweep, atau
            rescue, untuk siapa pun. Saldonya hanya bisa tetap atau bertambah.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-ink-muted">
            Kalau utility ini pernah punya versi dengan vault berbeda, total ekosistem harus
            menjumlahkan <strong>seluruh</strong> vault yang pernah terdaftar. Vault versi lama
            tetap memegang ASETQU-nya selamanya.
          </p>
        </Card>
      )}
    </div>
  );
}
